import { NameOptions } from "../extras/reactive-names";
import {
	ContextReactiveOptions,
	CreateReactive,
} from "./reactive-abstraction-types";
import { createReactiveArray } from "./reactive-array";
import { createReactiveMap } from "./reactive-map";
import { createReactiveObject } from "./reactive-object";
import { providePromiseAtomName } from "./reactive-promise";
import { createReactiveSet } from "./reactive-set";
import { getAllGetterKeys, isObject } from "./reactive-utils";

const inertProxyMap = new WeakMap<object, object>();

const proxyInertMap = new WeakMap<object, object>();

const isProxySet = new WeakSet<object>();

/**
 * Creates a reactive version of the provided object.
 * This is the primary entry point for making data structures reactive.
 * It supports objects, arrays, Sets, Maps, and Promises.
 *
 * @param value The object to make reactive.
 * @param reactiveOptions Optional configuration for the reactive instance.
 * @returns A reactive proxy of the input object, or the value itself if it cannot be made reactive.
 */
export const createReactive: CreateReactive = (value, reactiveOptions) => {
	const { name, ...otherReactiveOptions } = reactiveOptions ?? {};
	return createProxy(
		value,
		otherReactiveOptions,
		name ? { atomName: name } : undefined
	);
};

export const createProxy = <T>(
	value: T,
	reactiveOptions?: ContextReactiveOptions,
	nameOptions?: NameOptions
): T => {
	if (isObject(value) && Object.isFrozen(value)) {
		return ensureInert(value);
	}
	if (isObject(value)) {
		const proxy = getReactive(value);
		if (proxy) {
			return proxy;
		}
	}
	if (Array.isArray(value)) {
		const proxy = createReactiveArray(value, reactiveOptions, nameOptions);
		return setProxy(value, proxy);
	} else if (isObject(value)) {
		if (value instanceof Set) {
			const proxy = createReactiveSet(value, reactiveOptions, nameOptions);
			return setProxy(value, proxy);
		} else if (value instanceof Map) {
			const proxy = createReactiveMap(value, reactiveOptions, nameOptions);
			return setProxy(value, proxy);
		} else if (value instanceof Promise) {
			providePromiseAtomName(value, nameOptions);
			return value;
		} else {
			const proxy = createReactiveObject(value, reactiveOptions, nameOptions);
			return setProxy(value, proxy);
		}
	}
	return value;
};

export const setProxy = <T extends object>(inertObject: T, proxy: T): T => {
	inertProxyMap.set(inertObject, proxy);
	proxyInertMap.set(proxy, inertObject);
	isProxySet.add(proxy);
	return proxy;
};

/**
 * Checks if a given value is a reactive proxy.
 *
 * @param value The value to check.
 * @returns `true` if the value is a reactive proxy, `false` otherwise.
 */
export const isReactive = <T>(value: T): value is T & object => {
	return isProxySet.has(value as unknown as object);
};

/**
 * Retrieves the reactive proxy associated with a given object.
 * If the object itself is a proxy, it is returned.
 * If the object is an inert (original) object that has a reactive proxy, that proxy is returned.
 *
 * @param value The object for which to get the reactive proxy.
 * @returns The reactive proxy if one exists, otherwise `null`.
 */
export const getReactive = <T extends object>(value: T): T | null => {
	if (isProxySet.has(value)) {
		return value;
	} else {
		const proxy = inertProxyMap.get(value);
		if (proxy) {
			return proxy as T;
		}
	}
	return null;
};

/**
 * Retrieves the original, underlying non-reactive (inert) object from a reactive proxy.
 *
 * @param proxy The reactive proxy.
 * @returns The underlying inert object if `proxy` is a known reactive proxy, otherwise `null`.
 */
export const getInert = <T extends object>(proxy: T): T | null => {
	return (proxyInertMap.get(proxy) as T) ?? null;
};

/**
 * Ensures that the returned value is the inert (non-reactive) version of the input.
 * If the input is a reactive proxy, its underlying inert version is returned.
 * Otherwise, the input value is returned as is.
 *
 * @param value The value to ensure is inert.
 * @returns The underlying inert version of the value.
 */
export const ensureInert = <T>(value: T): T => {
	return (isObject(value) && getInert(value)) || value;
};

/**
 * Retrieves a set of keys that correspond to computed properties (getters) on a reactive object.
 * For non-object types or non-reactive objects, an empty set is returned.
 *
 * @param object The reactive object (or any value).
 * @returns A `Set` of string or symbol keys representing computed properties.
 */
export const getComputedKeys = <T>(object: T): Set<string | symbol> => {
	if (
		isObject(object) &&
		!Array.isArray(object) &&
		!(object instanceof Set) &&
		!(object instanceof Map)
	) {
		return getProxyComputedKeys(object);
	}
	return new Set();
};

export const getProxyComputedKeys = <T extends object>(proxy: T) => {
	return getAllGetterKeys(ensureInert(proxy));
};
